import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { IssuedPayroll } from '../models/issued-payroll.model';
import { IssueRejectedApiError } from '../services/pending-bonus-api.types';
import { PendingBonusRepository } from '../services/pending-bonus.repository';

export type BonusIssueStatus = 'idle' | 'submitting' | 'issued' | 'conflict' | 'error';

const ERROR_SUBJECT = 'la emisión de bonos';
const DEFAULT_REJECTION_MESSAGES: Readonly<Record<number, string>> = {
  409: 'Algunas de las cuentas seleccionadas ya pertenecen a una nómina.',
  422: 'Algunas de las cuentas seleccionadas ya no están pendientes de emisión.',
};

const isRejection = (cause: unknown): cause is HttpErrorResponse =>
  cause instanceof HttpErrorResponse && cause.status in DEFAULT_REJECTION_MESSAGES;

const conflictMessage = (cause: HttpErrorResponse): string =>
  (cause.error as Partial<IssueRejectedApiError> | null)?.error?.message ??
  DEFAULT_REJECTION_MESSAGES[cause.status];

const NOT_IMPLEMENTED_STATUS = 501;
const NOT_AVAILABLE_ERROR: AppError = {
  kind: 'server',
  tone: 'warning',
  title: 'La emisión de bonos aún no está disponible',
  description: 'Todavía no es posible emitir nóminas desde el portal. Intenta más adelante.',
  status: NOT_IMPLEMENTED_STATUS,
};

const toIssueError = (cause: unknown): AppError =>
  cause instanceof HttpErrorResponse && cause.status === NOT_IMPLEMENTED_STATUS
    ? NOT_AVAILABLE_ERROR
    : toAppError(cause, ERROR_SUBJECT);

const selectionKey = (folios: readonly string[]): string => [...folios].sort().join(',');

@Injectable()
export class BonusIssueStore {
  private readonly repository = inject(PendingBonusRepository);

  private readonly _status = signal<BonusIssueStatus>('idle');
  private readonly _payroll = signal<IssuedPayroll | null>(null);
  private readonly _conflictMessage = signal<string | null>(null);
  private readonly _error = signal<AppError | null>(null);

  private idempotency: { selection: string; key: string } | null = null;
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly payroll = this._payroll.asReadonly();
  readonly conflictMessage = this._conflictMessage.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isSubmitting = computed(() => this._status() === 'submitting');

  // Conserva la Idempotency-Key: si se salió con la emisión en curso y se reintenta la misma
  // selección, el BFF la reconoce en vez de tratarla como una emisión nueva.
  reset(): void {
    this.inFlight?.unsubscribe();
    this._payroll.set(null);
    this._conflictMessage.set(null);
    this._error.set(null);
    this._status.set('idle');
  }

  issue(folios: readonly string[]): void {
    if (!folios.length || this.isSubmitting()) return;

    this._payroll.set(null);
    this._conflictMessage.set(null);
    this._error.set(null);
    this._status.set('submitting');

    this.inFlight = this.repository.issue(folios, this.idempotencyKeyFor(folios)).subscribe({
      next: (payroll) => {
        this.idempotency = null;
        this._payroll.set(payroll);
        this._status.set('issued');
      },
      error: (cause: unknown) => {
        if (isRejection(cause)) {
          this.idempotency = null;
          this._conflictMessage.set(conflictMessage(cause));
          this._status.set('conflict');
          return;
        }
        this._error.set(toIssueError(cause));
        this._status.set('error');
      },
    });
  }

  private idempotencyKeyFor(folios: readonly string[]): string {
    const selection = selectionKey(folios);
    if (this.idempotency?.selection !== selection) {
      this.idempotency = { selection, key: crypto.randomUUID() };
    }
    return this.idempotency.key;
  }
}
